import { desc, eq, inArray, sql } from "drizzle-orm";
import { user } from "@/server/platform/db/schema/auth";
import {
  kycVerificationRequests,
  suspiciousFlags,
  topupRequests,
  wallets,
} from "@/server/platform/db/schema/wallet";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { resolveEffectiveSeverity } from "../resolve-effective-severity";
import { emitAdminNotification } from "./emit-admin-notification";
import type { DbTransaction } from "@/shared/types";

function severityForTopupKyc(
  type: string,
  createdAt: Date,
): "normal" | "warning" {
  return resolveEffectiveSeverity(type, "normal", createdAt);
}

async function resolveCustomerLabels(
  db: DbTransaction,
  rows: Array<{ ownerType: string; ownerId: string | null }>,
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  const customerIds = rows
    .filter((r) => r.ownerType === "customer" && r.ownerId)
    .map((r) => r.ownerId as string);

  if (customerIds.length === 0) return result;

  const users = await db
    .select({ id: user.id, name: user.name, email: user.email })
    .from(user)
    .where(sql`${user.id} in ${customerIds}`);

  for (const u of users) {
    result.set(u.id, u.name?.trim() || u.email || u.id);
  }

  return result;
}

async function syncTopupNotifications(db: DbTransaction): Promise<void> {
  const rows = await db
    .select({
      id: topupRequests.id,
      amount: topupRequests.amount,
      currency: topupRequests.currency,
      createdAt: topupRequests.createdAt,
      ownerType: wallets.ownerType,
      ownerId: wallets.ownerId,
    })
    .from(topupRequests)
    .innerJoin(wallets, eq(topupRequests.walletId, wallets.id))
    .where(eq(topupRequests.status, "submitted"))
    .orderBy(desc(topupRequests.createdAt))
    .limit(100);

  const labels = await resolveCustomerLabels(db, rows);

  for (const row of rows) {
    const ownerLabel =
      row.ownerId && labels.get(row.ownerId)
        ? labels.get(row.ownerId)!
        : row.ownerId?.slice(0, 12) ?? "—";
    const amount = Number(row.amount).toLocaleString("en-US");

    await emitAdminNotification(
      {
        type: "wallet.topup.submitted",
        title: "ຄຳຂໍເຕີມເງິນໃໝ່",
        body: `${ownerLabel} · ${amount} ${row.currency}`,
        entityType: "topup_request",
        entityId: row.id,
        href: `/app/wallet/topup/${row.id}`,
        severity: severityForTopupKyc("wallet.topup.submitted", row.createdAt),
        requiredPermission: Permissions.top_up.list,
        createdAt: row.createdAt,
      },
      db,
    );
  }
}

async function syncKycNotifications(db: DbTransaction): Promise<void> {
  const pendingStatuses = ["submitted", "under_review"] as const;
  const tierLabels: Record<string, string> = {
    basic: "ພື້ນຖານ",
    verified: "ຢືນຢັນ",
  };

  const rows = await db
    .select({
      id: kycVerificationRequests.id,
      requestedTier: kycVerificationRequests.requestedTier,
      fullName: kycVerificationRequests.fullName,
      createdAt: kycVerificationRequests.createdAt,
      customerName: user.name,
      customerEmail: user.email,
    })
    .from(kycVerificationRequests)
    .innerJoin(user, eq(kycVerificationRequests.userId, user.id))
    .where(inArray(kycVerificationRequests.status, [...pendingStatuses]))
    .orderBy(desc(kycVerificationRequests.createdAt))
    .limit(100);

  for (const row of rows) {
    const who =
      row.fullName?.trim() ||
      row.customerName?.trim() ||
      row.customerEmail ||
      "—";

    await emitAdminNotification(
      {
        type: "wallet.kyc.submitted",
        title: "ຄຳຂໍ KYC ໃໝ່",
        body: `${who} · ${tierLabels[row.requestedTier] ?? row.requestedTier}`,
        entityType: "kyc_verification_request",
        entityId: row.id,
        href: `/app/wallet/kyc-requests/${row.id}`,
        severity: severityForTopupKyc("wallet.kyc.submitted", row.createdAt),
        requiredPermission: Permissions.compliance.read,
        createdAt: row.createdAt,
      },
      db,
    );
  }
}

async function syncSuspiciousNotifications(db: DbTransaction): Promise<void> {
  const rows = await db
    .select({
      id: suspiciousFlags.id,
      reason: suspiciousFlags.reason,
      severity: suspiciousFlags.severity,
      createdAt: suspiciousFlags.createdAt,
    })
    .from(suspiciousFlags)
    .where(eq(suspiciousFlags.status, "pending"))
    .orderBy(desc(suspiciousFlags.createdAt))
    .limit(100);

  for (const row of rows) {
    const severity =
      row.severity === "critical"
        ? "critical"
        : row.severity === "high"
          ? "warning"
          : "normal";

    await emitAdminNotification(
      {
        type: "compliance.suspicious.pending",
        title: "ທຸລະກຳຜິນປົກກະຕິ",
        body: row.reason.slice(0, 200),
        entityType: "suspicious_flag",
        entityId: row.id,
        href: "/app/wallet/compliance",
        severity,
        requiredPermission: Permissions.suspicious.list,
        createdAt: row.createdAt,
      },
      db,
    );
  }
}

/** Backfill queue rows → admin_notifications (idempotent) */
export async function syncQueueNotifications(db: DbTransaction): Promise<void> {
  await syncTopupNotifications(db);
  await syncKycNotifications(db);
  await syncSuspiciousNotifications(db);
}
